
interface SecureStateData {
  appOrigin: string;
  timestamp: number;
  returnPath: string;
  cspAccountId: string;
  nonce: string;
  csrfToken: string;
}

interface RateLimitData {
  attempts: number;
  lastAttempt: number;
  cooldownUntil?: number;
}

const RATE_LIMIT_KEY = 'oauth_rate_limit';
const MAX_ATTEMPTS = 15; // Changed from 3 to 15
const RATE_LIMIT_WINDOW = 5 * 60 * 1000; // 5 minutes
const COOLDOWN_PERIOD = 10 * 60 * 1000; // 10 minutes

// Production-safe logging
const isDevelopment = import.meta.env.DEV;
const securityLog = (message: string, data?: any) => {
  if (isDevelopment) {
    console.log(`OAuth Security: ${message}`, data);
  }
};

const securityWarn = (message: string, data?: any) => {
  console.warn(`OAuth Security Warning: ${message}`, data);
};

const securityError = (message: string, data?: any) => {
  console.error(`OAuth Security Error: ${message}`, data);
};

// Enhanced CSRF token with additional entropy
const generateEnhancedNonce = (): string => {
  const timestamp = Date.now().toString();
  const random = crypto.randomUUID();
  const entropy = Math.random().toString(36).substring(2);
  return `${timestamp}-${random}-${entropy}`;
};

// Rate limiting implementation
export const checkRateLimit = (userId: string = 'anonymous'): { allowed: boolean; reason?: string } => {
  try {
    const rateLimitKey = `${RATE_LIMIT_KEY}_${userId}`;
    const stored = localStorage.getItem(rateLimitKey);
    const now = Date.now();
    
    if (!stored) {
      // First attempt
      const rateData: RateLimitData = {
        attempts: 1,
        lastAttempt: now
      };
      localStorage.setItem(rateLimitKey, JSON.stringify(rateData));
      return { allowed: true };
    }
    
    const rateData: RateLimitData = JSON.parse(stored);
    
    // Check if still in cooldown
    if (rateData.cooldownUntil && now < rateData.cooldownUntil) {
      const remainingMs = rateData.cooldownUntil - now;
      const remainingMin = Math.ceil(remainingMs / 60000);
      return { 
        allowed: false, 
        reason: `Rate limited. Try again in ${remainingMin} minute(s).` 
      };
    }
    
    // Reset window if enough time has passed
    if (now - rateData.lastAttempt > RATE_LIMIT_WINDOW) {
      const newRateData: RateLimitData = {
        attempts: 1,
        lastAttempt: now
      };
      localStorage.setItem(rateLimitKey, JSON.stringify(newRateData));
      return { allowed: true };
    }
    
    // Check if max attempts reached
    if (rateData.attempts >= MAX_ATTEMPTS) {
      const updatedRateData: RateLimitData = {
        ...rateData,
        cooldownUntil: now + COOLDOWN_PERIOD
      };
      localStorage.setItem(rateLimitKey, JSON.stringify(updatedRateData));
      return { 
        allowed: false, 
        reason: `Too many attempts (${MAX_ATTEMPTS}). Cooldown for ${COOLDOWN_PERIOD / 60000} minutes.` 
      };
    }
    
    // Increment attempts
    const updatedRateData: RateLimitData = {
      attempts: rateData.attempts + 1,
      lastAttempt: now
    };
    localStorage.setItem(rateLimitKey, JSON.stringify(updatedRateData));
    
    return { allowed: true };
  } catch (error) {
    securityError('Rate limit check failed', error);
    return { allowed: true }; // Fail open for UX
  }
};

export const clearRateLimit = (userId: string = 'anonymous'): void => {
  try {
    const rateLimitKey = `${RATE_LIMIT_KEY}_${userId}`;
    localStorage.removeItem(rateLimitKey);
    securityLog('Rate limit cleared for user', { userId });
  } catch (error) {
    securityError('Failed to clear rate limit', error);
  }
};

export const generateSecureState = (data: {
  appOrigin: string;
  returnPath: string;
  cspAccountId: string;
}): string => {
  const nonce = generateEnhancedNonce();
  const csrfToken = crypto.randomUUID();
  
  const secureState: SecureStateData = {
    ...data,
    timestamp: Date.now(),
    nonce,
    csrfToken
  };
  
  // Store CSRF token in both sessionStorage and localStorage for redundancy
  const csrfKey = `oauth_csrf_${nonce}`;
  sessionStorage.setItem(csrfKey, csrfToken);
  localStorage.setItem(csrfKey, JSON.stringify({
    token: csrfToken,
    timestamp: Date.now(),
    expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutes
  }));
  
  securityLog('Generated secure state with enhanced CSRF token', { nonce, csrfKey });
  
  return encodeURIComponent(JSON.stringify(secureState));
};

export const validateOAuthState = (stateParam: string): {
  isValid: boolean;
  data?: SecureStateData;
  error?: string;
} => {
  try {
    const data: SecureStateData = JSON.parse(decodeURIComponent(stateParam));
    
    securityLog('Validating state with enhanced security', { 
      nonce: data.nonce, 
      timestamp: data.timestamp,
      age: Date.now() - data.timestamp 
    });
    
    // Enhanced timeout - max 30 minutes old
    const maxAge = 30 * 60 * 1000; // 30 minutes
    if (Date.now() - data.timestamp > maxAge) {
      securityWarn('State parameter expired');
      return { isValid: false, error: 'State parameter expired' };
    }
    
    // Enhanced CSRF token validation with fallback mechanism
    const csrfKey = `oauth_csrf_${data.nonce}`;
    let storedToken = sessionStorage.getItem(csrfKey);
    let tokenSource = 'sessionStorage';
    
    // Fallback to localStorage if sessionStorage is missing
    if (!storedToken) {
      securityLog('CSRF token not found in sessionStorage, checking localStorage');
      const localStorageData = localStorage.getItem(csrfKey);
      if (localStorageData) {
        try {
          const parsed = JSON.parse(localStorageData);
          // Check if localStorage token is still valid
          if (parsed.expiresAt > Date.now()) {
            storedToken = parsed.token;
            tokenSource = 'localStorage';
            securityLog('Using CSRF token from localStorage fallback');
          } else {
            securityLog('localStorage CSRF token expired');
            localStorage.removeItem(csrfKey);
          }
        } catch (e) {
          securityError('Error parsing localStorage CSRF token', e);
          localStorage.removeItem(csrfKey);
        }
      }
    }
    
    // Reduced relaxed validation window from 5 to 2 minutes for better security
    if (!storedToken) {
      securityWarn('CSRF token not found in any storage');
      
      // For trusted scenarios, reduce the lenient window to 2 minutes
      const recentThreshold = 2 * 60 * 1000; // 2 minutes (reduced from 5)
      if (Date.now() - data.timestamp < recentThreshold) {
        securityLog('Allowing validation despite missing CSRF token (recent state under 2min)');
        return { isValid: true, data };
      }
      
      return { isValid: false, error: 'CSRF token validation failed - token not found' };
    }
    
    if (storedToken !== data.csrfToken) {
      securityError('CSRF token mismatch', { 
        stored: storedToken?.substring(0, 8) + '...', 
        received: data.csrfToken?.substring(0, 8) + '...',
        source: tokenSource
      });
      return { isValid: false, error: 'Invalid CSRF token - token mismatch' };
    }
    
    // Clean up used tokens from both storages
    sessionStorage.removeItem(csrfKey);
    localStorage.removeItem(csrfKey);
    
    securityLog('State validation successful using', tokenSource);
    return { isValid: true, data };
  } catch (error) {
    securityError('State validation error', error);
    return { isValid: false, error: 'Invalid state parameter format' };
  }
};

export const verifyOAuthOrigin = (expectedOrigin: string, actualOrigin?: string): boolean => {
  if (!actualOrigin) {
    securityWarn('No origin provided for verification');
    return false;
  }
  
  const isValid = expectedOrigin === actualOrigin;
  securityLog('Origin verification', { expected: expectedOrigin, actual: actualOrigin, isValid });
  return isValid;
};

export const isTimestampValid = (timestamp: number, maxAgeMs: number = 30 * 60 * 1000): boolean => {
  const isValid = Date.now() - timestamp <= maxAgeMs;
  securityLog('Timestamp validation', { timestamp, age: Date.now() - timestamp, maxAge: maxAgeMs, isValid });
  return isValid;
};

// Enhanced security cleanup function
export const securityCleanup = (): void => {
  try {
    // Clean up expired CSRF tokens
    const keysToCheck = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('oauth_csrf_')) {
        keysToCheck.push(key);
      }
    }
    
    keysToCheck.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
            localStorage.removeItem(key);
            securityLog('Cleaned up expired CSRF token', { key });
          }
        }
      } catch (e) {
        // Remove invalid entries
        localStorage.removeItem(key);
        securityLog('Cleaned up invalid CSRF token entry', { key });
      }
    });
    
    // Clean up rate limit data older than 24 hours
    const rateLimitKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(RATE_LIMIT_KEY)) {
        rateLimitKeys.push(key);
      }
    }
    
    rateLimitKeys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed: RateLimitData = JSON.parse(data);
          const dayOld = 24 * 60 * 60 * 1000;
          if (Date.now() - parsed.lastAttempt > dayOld) {
            localStorage.removeItem(key);
            securityLog('Cleaned up old rate limit data', { key });
          }
        }
      } catch (e) {
        localStorage.removeItem(key);
        securityLog('Cleaned up invalid rate limit entry', { key });
      }
    });
  } catch (error) {
    securityError('Security cleanup failed', error);
  }
};
